import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { useAuth } from '../context/AuthContext'
import { useTheme } from '../context/ThemeContext'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Textarea } from '../components/ui/textarea'
import { Label } from '../components/ui/label'
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog'
import { toast } from 'sonner'
import { Upload, Camera, ArrowLeft, Save, Shield, CheckCircle, Lock, AlertCircle, Loader2, AlertTriangle, Copy, Info, Mail } from 'lucide-react'
import DarkModeToggle from '../components/DarkModeToggle'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL
const API = `${BACKEND_URL}/api`

export default function EditProfile() {
  const navigate = useNavigate()
  const { token, address } = useAuth()
  const { isDarkMode } = useTheme()
  const [formData, setFormData] = useState({
    name: '',
    bio: '',
    facebook: '',
    instagram: '',
    twitter: '',
    linkedin: '',
    reddit: '',
    youtube: '',
    tiktok: '',
    website: '',
  })
  const [currentImage, setCurrentImage] = useState(null)
  const [newProfileImage, setNewProfileImage] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [confirmedLinks, setConfirmedLinks] = useState([])
  const [verificationStatus, setVerificationStatus] = useState('unverified')
  const [showSubmitDialog, setShowSubmitDialog] = useState(false)
  const [contactEmail, setContactEmail] = useState('')
  const [isSubmittingReview, setIsSubmittingReview] = useState(false)
  const [myProfile, setMyProfile] = useState(null)
  const [username, setUsername] = useState('')

  useEffect(() => {
    fetchProfile()
  }, [])

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/profiles/me`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      const profile = response.data
      const socialLinks = profile.social_links || {}
      
      setFormData({
        name: profile.name || '',
        bio: profile.bio || '',
        facebook: socialLinks.facebook || '',
        instagram: socialLinks.instagram || '',
        twitter: socialLinks.twitter || '',
        linkedin: socialLinks.linkedin || '',
        reddit: socialLinks.reddit || '',
        youtube: socialLinks.youtube || '',
        tiktok: socialLinks.tiktok || '',
        website: socialLinks.website || '',
      })
      setCurrentImage(profile.profile_image)
      setImagePreview(profile.profile_image)
      setConfirmedLinks(profile.confirmed_links || [])
      setVerificationStatus(profile.verification_status || 'unverified')
      setContactEmail(profile.contact_email || '')
      setMyProfile(profile)
      setUsername(profile.username)
    } catch (err) {
      console.error('Fetch profile error:', err)
      toast.error('Failed to load profile')
      navigate('/home')
    } finally {
      setIsLoading(false)
    }
  }

  const handleImageChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image too large (max 5MB)')
        e.target.value = null
        return
      }
      
      setNewProfileImage(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setImagePreview(reader.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSaving(true)

    try {
      const social_links = Object.fromEntries(
        Object.entries({
          facebook: formData.facebook,
          instagram: formData.instagram,
          twitter: formData.twitter,
          linkedin: formData.linkedin,
          reddit: formData.reddit,
          youtube: formData.youtube,
          tiktok: formData.tiktok,
          website: formData.website,
        }).map(([key, value]) => [key, value || null])
      )

      await axios.put(
        `${API}/profiles/me`,
        {
          name: formData.name,
          bio: formData.bio || null,
          social_links,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )

      if (newProfileImage) {
        const formDataImage = new FormData()
        formDataImage.append('file', newProfileImage)
        
        await axios.post(`${API}/profiles/upload-image`, formDataImage, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        })
      }

      toast.success('Profile updated successfully!')
      await fetchProfile()
      setNewProfileImage(null)
      navigate('/home')
    } catch (err) {
      console.error('Update profile error:', err)
      const errorMessage = err.response?.data?.detail || 'Failed to update profile'
      toast.error(errorMessage)
    } finally {
      setIsSaving(false)
    }
  }

  const handleConfirmLink = async (linkType) => {
    const linkUrl = formData[linkType]
    if (!linkUrl || linkUrl.trim() === '') {
      toast.error('Please enter a URL before confirming')
      return
    }
    if (isLinkConfirmed(linkType)) {
      toast.error('This link is already confirmed.')
      return
    }

    setIsSaving(true)
    try {
      const social_links = Object.fromEntries(
        Object.entries({
          facebook: formData.facebook,
          instagram: formData.instagram,
          twitter: formData.twitter,
          linkedin: formData.linkedin,
          reddit: formData.reddit,
          youtube: formData.youtube,
          tiktok: formData.tiktok,
          website: formData.website,
        }).map(([key, value]) => [key, value || null])
      )

      await axios.put(
        `${API}/profiles/me`,
        {
          name: formData.name,
          bio: formData.bio || null,
          social_links,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )

      const response = await axios.post(
        `${API}/verification/confirm-link`,
        null,
        {
          params: { link_type: linkType },
          headers: { Authorization: `Bearer ${token}` }
        }
      )
      
      toast.success(`${linkType} link confirmed and locked!`)
      setConfirmedLinks([...confirmedLinks, linkType])
      await fetchProfile()
    } catch (err) {
      console.error('Confirm link error:', err)
      toast.error(err.response?.data?.detail || 'Failed to confirm link')
    } finally {
      setIsSaving(false)
    }
  }

  const handleSubmitForReview = async () => {
    if (!contactEmail || !contactEmail.includes('@')) {
      toast.error('Please enter a valid email address')
      return
    }

    setIsSubmittingReview(true)
    try {
      await axios.post(
        `${API}/verification/submit-review`,
        { contact_email: contactEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      toast.success('Profile submitted for manual review! You will be contacted within 5 business days.')
      setShowSubmitDialog(false)
      setVerificationStatus('pending_review')
      await fetchProfile()
    } catch (err) {
      console.error('Submit review error:', err)
      const errorMessage = err.response?.data?.detail || 'Failed to submit for review'
      toast.error(errorMessage)
    } finally {
      setIsSubmittingReview(false)
    }
  }

  const getInitials = (name) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  const isLinkConfirmed = (linkType) => confirmedLinks.includes(linkType)

  const profileLink = `${window.location.origin}/profile/${username}`

  const copyProfileLink = () => {
    navigator.clipboard.writeText(profileLink)
    toast.success('Profile link copied to clipboard!')
  }

  const socialLinkFields = [
    { key: 'facebook', label: 'Facebook', placeholder: 'https://facebook.com/yourprofile' },
    { key: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/yourprofile' },
    { key: 'twitter', label: 'X (Twitter)', placeholder: 'https://x.com/yourprofile' },
    { key: 'linkedin', label: 'LinkedIn', placeholder: 'https://linkedin.com/in/yourprofile' },
    { key: 'reddit', label: 'Reddit', placeholder: 'https://reddit.com/u/yourprofile' },
    { key: 'youtube', label: 'YouTube', placeholder: 'https://youtube.com/@yourprofile' },
    { key: 'tiktok', label: 'TikTok', placeholder: 'https://tiktok.com/@yourprofile' },
    { key: 'website', label: 'Website', placeholder: 'https://yourwebsite.com' },
  ]

  if (isLoading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50'}`}>
        <Loader2 className="w-12 h-12 text-indigo-600 animate-spin" />
      </div>
    )
  }

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50'} py-12 px-4`}>
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <Button
            data-testid="back-button"
            onClick={() => navigate('/home')}
            variant="ghost"
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <DarkModeToggle />
        </div>

        {/* Verification Status Banner */}
        {verificationStatus === 'unverified' && (
          <div className={`mb-6 p-4 rounded-xl border ${isDarkMode ? 'bg-orange-900/40 border-orange-700' : 'bg-orange-50 border-orange-200'}`}>
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className={`font-semibold ${isDarkMode ? 'text-orange-200' : 'text-orange-800'}`}>Profile Verification Required</h3>
                <p className={`text-sm mt-1 ${isDarkMode ? 'text-orange-300' : 'text-orange-700'}`}>
                  To enable withdrawals, confirm at least 3 social links and submit for manual review.
                </p>
                <p className={`text-sm mt-2 font-bold ${isDarkMode ? 'text-orange-200' : 'text-orange-800'}`}>
                  Confirmed Links: {confirmedLinks.length}/3 minimum
                </p>
              </div>
            </div>
          </div>
        )}

        {verificationStatus === 'pending_review' && (
          <div className={`mb-6 p-4 rounded-xl border ${isDarkMode ? 'bg-blue-900/40 border-blue-700' : 'bg-blue-50 border-blue-200'}`}>
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className={`font-semibold ${isDarkMode ? 'text-blue-200' : 'text-blue-800'}`}>Verification Pending Review</h3>
                <p className={`text-sm mt-1 ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                  Your profile is under manual review. This process takes up to 5 business days. You will be contacted via email.
                </p>
              </div>
            </div>
          </div>
        )}

        {verificationStatus === 'verified' && (
          <div className={`mb-6 p-4 rounded-xl border ${isDarkMode ? 'bg-green-900/40 border-green-700' : 'bg-green-50 border-green-200'}`}>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className={`font-semibold ${isDarkMode ? 'text-green-200' : 'text-green-800'}`}>Profile Verified Successfully</h3>
                <p className={`text-sm mt-1 ${isDarkMode ? 'text-green-300' : 'text-green-700'}`}>
                  Your profile is fully verified. Withdrawals are enabled.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Instruction Box */}
        {verificationStatus === 'unverified' && (
          <div className={`mb-6 p-4 rounded-xl border ${isDarkMode ? 'bg-indigo-900/40 border-indigo-700' : 'bg-indigo-50 border-indigo-200'}`}>
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className={`font-semibold ${isDarkMode ? 'text-indigo-200' : 'text-indigo-800'}`}>Verification Instructions</h3>
                <p className={`text-sm mt-2 ${isDarkMode ? 'text-indigo-300' : 'text-indigo-700'}`}>
                  Add your Public Respect profile link to your social media bios or website:
                </p>
                <div className={`mt-3 p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-white'} flex items-center justify-between gap-2`}>
                  <code className={`text-sm break-all ${isDarkMode ? 'text-indigo-300' : 'text-indigo-600'}`}>
                    {profileLink}
                  </code>
                  <Button onClick={copyProfileLink} size="sm" variant="outline" className="flex-shrink-0">
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <p className={`text-xs mt-2 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>
                  Our review team will check if this link is present in your social media profiles or website.
                </p>
              </div>
            </div>
          </div>
        )}

        <Card className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white/95 border-purple-100'} backdrop-blur-sm`}>
          <CardHeader>
            <CardTitle className={`text-3xl font-extrabold ${isDarkMode ? 'text-gray-100' : 'bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent'}`} style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              Edit Profile
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form data-testid="edit-profile-form" onSubmit={handleSubmit} className="space-y-6">
              {/* Profile Image Upload */}
              <div className="flex flex-col items-center mb-6">
                <Label className={`mb-3 ${isDarkMode ? 'text-gray-200' : ''}`}>Profile Image</Label>
                <div className="relative">
                  <Avatar className={`w-32 h-32 border-4 ${isDarkMode ? 'border-purple-400' : 'border-purple-200'}`}>
                    {imagePreview ? (
                      <AvatarImage src={imagePreview} alt="Profile" />
                    ) : (
                      <AvatarFallback className="text-3xl font-bold bg-gradient-to-br from-indigo-500 to-purple-500 text-white">
                        {formData.name ? getInitials(formData.name) : <Camera className="w-12 h-12" />}
                      </AvatarFallback>
                    )}
                  </Avatar>
                  <label htmlFor="profile-image" className="absolute bottom-0 right-0 bg-indigo-600 hover:bg-indigo-700 text-white p-2 rounded-full cursor-pointer shadow-lg transition-all transform hover:scale-105">
                    <Upload className="w-5 h-5" />
                    <input
                      id="profile-image"
                      data-testid="profile-image-input"
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                    />
                  </label>
                </div>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mt-2`}>Max size: 5MB</p>
              </div>

              <div>
                <Label htmlFor="name" className={isDarkMode ? 'text-gray-200' : ''}>Name *</Label>
                <Input
                  data-testid="profile-name-input"
                  id="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={`mt-1 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : ''}`}
                />
              </div>

              <div>
                <Label htmlFor="bio" className={isDarkMode ? 'text-gray-200' : ''}>Bio</Label>
                <Textarea
                  data-testid="profile-bio-input"
                  id="bio"
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  className={`mt-1 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : ''}`}
                  rows={3}
                  placeholder="Tell us about yourself"
                />
              </div>

              <div className={`border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} pt-6`}>
                <h3 className={`text-lg font-extrabold mb-2 ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`} style={{fontFamily: 'Space Grotesk, sans-serif'}}>Social Links Confirmation</h3>
                <div className={`mb-4 p-3 rounded-lg ${isDarkMode ? 'bg-blue-900/30 border border-blue-700' : 'bg-blue-50 border border-blue-200'}`}>
                  <p className={`text-sm ${isDarkMode ? 'text-blue-200' : 'text-blue-800'}`}>
                    <strong>Important:</strong> Only confirmed and locked social links will be visible on your profile after verification. 
                    Please confirm all links you want to display publicly.
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-4">
                  {socialLinkFields.map((field) => {
                    const confirmed = isLinkConfirmed(field.key)
                    const linkValue = formData[field.key]
                    // Links are only locked after verification is submitted for review
                    const isLocked = (verificationStatus === 'pending_review' || verificationStatus === 'verified') && confirmed
                    return (
                      <div key={field.key}>
                        <Label htmlFor={field.key} className={`flex items-center gap-2 ${isDarkMode ? 'text-gray-200' : ''}`}>
                          {field.label}
                          {confirmed && <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />}
                          {isLocked && <Lock className="w-3 h-3 text-gray-500 flex-shrink-0" />}
                        </Label>
                        <div className="flex gap-2 mt-1">
                          <Input
                            data-testid={`profile-${field.key}-input`}
                            id={field.key}
                            type="url"
                            value={linkValue}
                            onChange={(e) => setFormData({ ...formData, [field.key]: e.target.value })}
                            className={`flex-1 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : ''} ${isLocked ? 'opacity-75' : ''}`}
                            placeholder={field.placeholder}
                            disabled={isLocked || isSaving}
                          />
                          {isLocked ? (
                            <Button
                              type="button"
                              disabled
                              className="bg-green-600 text-white px-4 flex items-center gap-2"
                            >
                              <Lock className="w-4 h-4" />
                              Locked
                            </Button>
                          ) : confirmed ? (
                            <Button
                              type="button"
                              className="bg-green-600 text-white px-4 flex items-center gap-2"
                              disabled
                            >
                              <CheckCircle className="w-4 h-4" />
                              Ready
                            </Button>
                          ) : (
                            <Button
                              type="button"
                              onClick={() => handleConfirmLink(field.key)}
                              className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 flex items-center gap-2"
                              disabled={!linkValue || linkValue.trim() === '' || isSaving}
                            >
                              <CheckCircle className="w-4 h-4" />
                              Confirm
                            </Button>
                          )}
                        </div>
                        {confirmed && !isLocked && (
                          <p className="text-xs text-amber-600 mt-1">
                            Link confirmed but still editable. Will be locked after submitting for review.
                          </p>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Submit for Review Button */}
              {verificationStatus === 'unverified' && confirmedLinks.length >= 3 && (
                <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-green-900/40 border-green-700' : 'bg-green-50 border-green-200'}`}>
                  <div className={`flex items-start gap-3 mb-3 ${isDarkMode ? 'text-green-200' : 'text-green-800'}`}>
                    <AlertTriangle className='w-5 h-5 flex-shrink-0' />
                    <p className="text-sm font-medium">
                      You have confirmed {confirmedLinks.length} links! Submit your profile for manual verification.
                    </p>
                  </div>
                  <Button
                    type="button"
                    onClick={() => setShowSubmitDialog(true)}
                    className="w-full bg-green-600 hover:bg-green-700 text-white font-bold transition-transform transform hover:scale-[1.01]"
                  >
                    <Shield className="w-5 h-5 mr-2" />
                    Submit for Manual Review
                  </Button>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/home')}
                  className={`flex-1 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100 hover:bg-gray-600' : ''}`}
                  disabled={isSaving || isSubmittingReview}
                >
                  Cancel
                </Button>
                <Button
                  data-testid="save-profile-button"
                  type="submit"
                  disabled={isSaving || isSubmittingReview || !formData.name.trim()}
                  className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-bold transition-transform transform hover:scale-[1.01]"
                >
                  {isSaving ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Saving...</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Save className="w-4 h-4" />
                      <span>Save Changes</span>
                    </div>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Submit for Review Dialog */}
      <Dialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <DialogContent className={isDarkMode ? 'bg-gray-800 border-gray-700' : ''}>
          <DialogHeader>
            <DialogTitle className={isDarkMode ? 'text-gray-100' : ''}>Submit for Manual Review</DialogTitle>
            <DialogDescription className={isDarkMode ? 'text-gray-400' : ''}>
              Our review team will verify your social links within 5 business days.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className={`p-3 rounded-lg border ${isDarkMode ? 'bg-red-900/30 border-red-700' : 'bg-red-50 border-red-200'}`}>
              <p className={`text-sm ${isDarkMode ? 'text-red-200' : 'text-red-800'}`}>
                <strong>⚠️ Important:</strong> Public Respect review team has full authority to reject or ban profiles if any fraud, impersonation, or suspicious activity is detected.
              </p>
            </div>
            
            <div>
              <Label htmlFor="contact-email" className={isDarkMode ? 'text-gray-200' : ''}>Contact Email *</Label>
              <div className="flex items-center gap-2 mt-1">
                <Mail className="w-5 h-5 text-gray-400" />
                <Input
                  id="contact-email"
                  type="email"
                  required
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder="your.email@example.com"
                  className={isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : ''}
                />
              </div>
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                We'll contact you at this email for clarifications or updates.
              </p>
            </div>

            <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
              <p className={`text-sm ${isDarkMode ? 'text-blue-200' : 'text-blue-800'}`}>
                <strong>Review Process:</strong> Our team will check if your Public Respect profile link is present in your confirmed social media profiles or website.
              </p>
            </div>

            <Button
              onClick={handleSubmitForReview}
              disabled={isSubmittingReview || !contactEmail}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-bold"
            >
              {isSubmittingReview ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Shield className="w-4 h-4 mr-2" />
                  Submit for Review
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
